import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormCheck } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { ApplicationApi } from "../../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../../utils/types";

interface Props {
	form: UseFormReturn<ApplicationApi>;
	updatedConfig: Record<string, any> | undefined;
	updateConfig: (prop: string, obj?: any) => void;
	appState: AppState;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<ApplicationApi>[]>>;
}

const GoOrderShopConfigurationForm = ({ form, updatedConfig, updateConfig, appState, setErrors }: Props) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const {
		formState: { errors },
		control,
		register,
		watch,
	} = form;
	const paymentMethodValidationConfig = useMemo(
		() => ({
			types: [
				{
					constraint: CustomValidationConstraint.REQUIRED,
				},
			],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const searchPaymentMethods = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getPaymentMethodsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const searchItems = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getItemsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	return (
		<>
			<div className="my-3">
				<h6>{t("modules.app.field.store_configuration.title")}</h6>
				<FormSelectGroup
					name="settings.payment_method_id"
					getOptionValue={(option) => option.id}
					path={`/${organization.id}/settings/payment_methods/`}
					label={t("modules.app.field.default_payment_method.title")}
					errors={errors}
					defaultValue={{
						label: updatedConfig?.payment_method_name,
						id: appState.app.settings.payment_method_id,
					}}
					loadOptions={searchPaymentMethods}
					control={control}
					onChange={(_, fullObj) => updateConfig("payment_method_name", fullObj)}
					data-testid="settings.payment_method_id"
					customValidationConfig={paymentMethodValidationConfig}
				/>
				<FormSelectGroup
					name="settings.delivery_item_id"
					getOptionValue={(option) => option.id}
					label={t("modules.app.field.delivery_item_id.title")}
					errors={errors}
					defaultValue={{
						label: appState.config?.delivery_item_name,
						id: appState.app.settings.delivery_item_id,
					}}
					loadOptions={searchItems}
					control={control}
					data-testid="settings.delivery_item_id"
				/>
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.app.action.overwrite_all_configurations.title")}
					help={t("modules.app.action.overwrite_all_configurations.help_text.title")}
					name="settings.overwrite_all_configurations"
					errors={errors}
				/>
				{!watch("settings.overwrite_all_configurations") && (
					<>
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.app.action.export_availabilities.title")}
							name="settings.export_availabilities"
							errors={errors}
						/>
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.app.action.export_store_configuration.title")}
							name="settings.export_store_configuration"
							errors={errors}
						/>
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.app.action.export_company_info.title")}
							name="settings.export_company_info"
							errors={errors}
						/>
					</>
				)}
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.app.action.export_menu_discounts.title")}
					name="settings.export_menu_discounts"
					errors={errors}
				/>
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.app.field.export_payment_methods.title")}
					name="settings.export_payment_methods"
					errors={errors}
				/>
			</div>
			<hr />
		</>
	);
};

export default GoOrderShopConfigurationForm;
